import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { expressMutation, expressQuery, getQueryKey } from '../../../../../repository/react-query/useExpress';
import { NotificationContext } from '../../../../../NotificationContext';
import { restoreCache, setDeletedQueryCache, updateQueryCache } from '../../../../../data/optimistic-update-utils';
import { merge } from 'lodash';
import { GEOFENCE_QUERY_KEY } from '../../../../../custom-components/geofences/Geofence.queries';

const baseQueryKey = 'wash-facility';
const baseEndpoint = '/wash-facilities';

/**
 * Fetch all washes
 */
export const useGetWashFacilities = ({ queryParams, enabled = true } = {}) => {
    const defaultParams = { where: { deleted_at: { eq: null } } };
    const params = merge(defaultParams, queryParams);
    return useQuery({
        queryKey: [baseQueryKey, getQueryKey(params)],
        queryFn: () => expressQuery({ endpoint: baseEndpoint, params: merge(defaultParams, params) }),
        enabled,
    });
};

/*
 * Create a wash facility record
 *
 * @example
 * const { mutate, isLoading } = useCreateWashFacility();
 *
 * // Create a wash facility
 * mutate({
 *   name: "Test"
 * });
 */
export const useCreateWashFacility = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: (body) =>
            expressMutation({
                method: 'post',
                endpoint: baseEndpoint,
                body,
            }),
        onError: () => {
            showSnackbar({ severity: 'error', message: 'Failed to create wash facility' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Successfully created wash facility' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};

/**
 * Updates a wash facility record
 *
 * @example
 * const { mutate, isLoading } = useMutateWashFacility();
 *
 * // Update a wash
 * mutate({
 *   _id: "12345",
 *   name: "Test 2"
 * });
 */
export const useMutateWashFacility = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id, ...body }) =>
            expressMutation({
                method: 'patch',
                endpoint: `${baseEndpoint}/${_id}`,
                body,
            }),
        onMutate: async (updatedItem) => updateQueryCache(queryClient, [baseQueryKey], updatedItem),
        onError: (_err, _, ctx) => {
            restoreCache(queryClient, ctx);
            showSnackbar({ severity: 'error', message: 'An error occurred while updating the wash facility. Please try again or contact support if the problem persists.' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Successfully updated wash facility' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};

/**
 * Soft deletes wash records.
 *
 * @example
 * const { mutate, isLoading } = useDeleteWash();
 *
 * // Delete a wash facility
 * mutate(id);
 */
export const useDeleteWashFacility = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: (id) =>
            expressMutation({
                method: 'delete',
                endpoint: `${baseEndpoint}/${id}`,
            }),
        onMutate: async (updatedItem) => setDeletedQueryCache(queryClient, [baseQueryKey], { id: updatedItem }),
        onError: (err, _, ctx) => {
            restoreCache(queryClient, ctx);
            const res = err?.response?.data;
            showSnackbar({ severity: 'error', message: res?.message ?? 'Failed to delete wash facility' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Successfully deleted wash facility' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};
